import { c as _c } from "react/compiler-runtime";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import type { AppProps } from 'antd';
import { App, message as antdMessage, notification as antdNotification } from 'antd';
import type { ConfigOptions, JointContent, ArgsProps as MessageArgsProps, MessageInstance, NoticeType } from 'antd/es/message/interface';
// import type { ModalStaticFunctions } from 'antd/es/modal/confirm'
import type { ArgsProps as AntdNotificationArgsProps, NotificationConfig } from 'antd/es/notification/interface';
import cx from 'classnames';
import React from 'react';
import './styles.scss';
export interface NotificationArgsProps extends AntdNotificationArgsProps {
  size?: 'lg';
}
type StaticFn = (args: NotificationArgsProps) => void;
export interface NotificationInstance {
  success: StaticFn;
  error: StaticFn;
  info: StaticFn;
  warning: StaticFn;
  open: StaticFn;
  destroy(key?: React.Key): void;
}
export type { ConfigOptions as MessageConfig, NotificationConfig, AppProps as AntdAppProps, MessageArgsProps };
const messagePrefixClass = 'app-message-notice';
const notificationPrefixClass = 'app-notification-notice';
const successMessageConfig: Partial<MessageArgsProps> = {
  // Empty string with a space is to avoid the default antd icon to be displayed
  icon: ' ',
  className: `${messagePrefixClass} ${messagePrefixClass}-success`
};
const errorMessageConfig: Partial<MessageArgsProps> = {
  className: `${messagePrefixClass} ${messagePrefixClass}-error`
};
const infoMessageConfig: Partial<MessageArgsProps> = {
  className: `${messagePrefixClass} ${messagePrefixClass}-info`
};
const loadingMessageConfig: Partial<MessageArgsProps> = {
  className: `${messagePrefixClass} ${messagePrefixClass}-loading`
};
const warningMessageConfig: Partial<MessageArgsProps> = {
  className: `${messagePrefixClass} ${messagePrefixClass}-warning`
};
const getMessageConfig = (type: NoticeType | undefined) => {
  switch (type) {
    case 'success':
      return successMessageConfig;
    case 'error':
      return errorMessageConfig;
    case 'info':
      return infoMessageConfig;
    case 'warning':
      return warningMessageConfig;
    case 'loading':
      return loadingMessageConfig;
    case undefined:
    default:
      return {};
  }
};
const successNotificationConfig: Partial<NotificationArgsProps> = {
  // Empty string with a space is to avoid the default antd icon to be displayed
  placement: 'bottom',
  className: `${notificationPrefixClass} ${notificationPrefixClass}-success`
};
const errorNotificationConfig: Partial<NotificationArgsProps> = {
  icon: <FontAwesomeIcon icon={faExclamationCircle} />,
  // Error notification are designed to be dismissed but to avoid being annoying we
  // add a long duration to eventually close it
  duration: 15,
  placement: 'bottom',
  className: `${notificationPrefixClass} ${notificationPrefixClass}-error`
};
const infoNotificationConfig: Partial<NotificationArgsProps> = {
  placement: 'bottom',
  className: `${notificationPrefixClass} ${notificationPrefixClass}-info`
};
const warningNotificationConfig: Partial<NotificationArgsProps> = {
  placement: 'bottom',
  className: `${notificationPrefixClass} ${notificationPrefixClass}-warning`
};
export const notification: typeof antdNotification = {
  ...antdNotification,
  useNotification: (): ReturnType<typeof antdNotification.useNotification> => {
    const [api, contextHolder] = antdNotification.useNotification();
    const originalError = api.error.bind(this);
    api.error = ({
      className,
      ...contentProps
    }) => {
      originalError({
        ...errorNotificationConfig,
        ...contentProps,
        className: cx(errorNotificationConfig.className, className)
      });
    };
    const originalSuccess = api.success.bind(this);
    api.success = ({
      className,
      ...contentProps
    }) => {
      originalSuccess({
        ...successNotificationConfig,
        ...contentProps,
        className: cx(successNotificationConfig.className, className)
      });
    };
    const originalInfo = api.info.bind(this);
    api.info = ({
      className,
      ...contentProps
    }) => {
      originalInfo({
        ...infoNotificationConfig,
        ...contentProps,
        className: cx(infoNotificationConfig.className, className)
      });
    };
    const originalWarning = api.warning.bind(this);
    api.warning = ({
      className,
      ...contentProps
    }) => {
      originalWarning({
        ...warningNotificationConfig,
        ...contentProps,
        className: cx(warningNotificationConfig.className, className)
      });
    };
    return [api, contextHolder];
  }
};
const generateMessageConfig = (config: Partial<MessageArgsProps>, content: JointContent): MessageArgsProps => isContentProps(content) ? {
  duration: 5,
  ...config,
  ...content,
  className: cx(config.className, content.className)
} : {
  duration: 5,
  ...config,
  content
};
export const message: typeof antdMessage = {
  ...antdMessage,
  useMessage: (): ReturnType<typeof antdMessage.useMessage> => {
    const [api, contextHolder] = antdMessage.useMessage();
    const originalError = api.error.bind(this);
    api.error = content => originalError(generateMessageConfig(errorMessageConfig, content));
    const originalSuccess = api.success.bind(this);
    api.success = content => originalSuccess(generateMessageConfig(successMessageConfig, content));
    const originalInfo = api.info.bind(this);
    api.info = content => originalInfo(generateMessageConfig(infoMessageConfig, content));
    const originalLoading = api.loading.bind(this);
    api.loading = content => originalLoading(generateMessageConfig(loadingMessageConfig, content));
    const originalWarning = api.warning.bind(this);
    api.warning = content => originalWarning(generateMessageConfig(warningMessageConfig, content));
    const originalOpen = api.open.bind(this);
    api.open = content => originalOpen(generateMessageConfig(getMessageConfig(content.type), content));
    return [api, contextHolder];
  }
};
const PrefixCls = 'app-container';
const isContentProps = (content: JointContent): content is MessageArgsProps => typeof content === 'object' && 'content' in (content || {});

/**
 * This class is a wrap around [antd App](https://ant.design/components/app/) component extending some of
 * the properties to follow our styling guidelines.
 *
 * Please refer to antd documentation for more details.
 *
 */
export function AntdApp(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx(PrefixCls, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <App {...props} className={t1} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
const useApp = () => {
  const $ = _c(24);
  const {
    message: appMessage,
    modal,
    notification: appNotification
  } = App.useApp();
  let t0;
  if ($[0] !== appNotification) {
    t0 = Object.prototype.hasOwnProperty.call(appNotification, "error") ? appNotification : antdNotification;
    $[0] = appNotification;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const internalNotification = t0;
  let t1;
  if ($[2] !== appMessage) {
    t1 = Object.prototype.hasOwnProperty.call(appMessage, "error") ? appMessage : antdMessage;
    $[2] = appMessage;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const internalMessage = t1;
  let t2;
  if ($[4] !== internalMessage) {
    t2 = {
      error: (content, duration, onClose) => internalMessage.error(generateMessageConfig(errorMessageConfig, content), duration, onClose),
      success: (content_0, duration_0, onClose_0) => internalMessage.success(generateMessageConfig(successMessageConfig, content_0), duration_0, onClose_0),
      destroy: (...t3) => {
        const args = t3;
        return internalMessage.destroy(...args);
      },
      info: (content_1, duration_1, onClose_1) => internalMessage.info(generateMessageConfig(infoMessageConfig, content_1), duration_1, onClose_1),
      loading: (content_2, duration_2, onClose_2) => internalMessage.loading(generateMessageConfig(loadingMessageConfig, content_2), duration_2, onClose_2),
      open: (...t4) => {
        const args_0 = t4;
        return internalMessage.open(...args_0);
      },
      warning: (content_3, duration_3, onClose_3) => internalMessage.warning(generateMessageConfig(warningMessageConfig, content_3), duration_3, onClose_3)
    };
    $[4] = internalMessage;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const message = t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[6] !== internalNotification) {
    t3 = t7 => {
      const {
        className,
        size,
        ...contentProps
      } = t7;
      internalNotification.success({
        ...successNotificationConfig,
        className: cx(successNotificationConfig.className, size && `${notificationPrefixClass}--${size}`, className),
        ...contentProps
      });
    };
    t4 = t8 => {
      const {
        className: className_0,
        size: size_0,
        ...contentProps_0
      } = t8;
      internalNotification.error({
        ...errorNotificationConfig,
        className: cx(errorNotificationConfig.className, size_0 && `${notificationPrefixClass}--${size_0}`, className_0),
        ...contentProps_0
      });
    };
    t5 = (...t9) => {
      const args_1 = t9;
      return internalNotification.destroy(...args_1);
    };
    t6 = t10 => {
      const {
        className: className_1,
        size: size_1,
        ...contentProps_1
      } = t10;
      internalNotification.info({
        ...infoNotificationConfig,
        className: cx(infoNotificationConfig.className, size_1 && `${notificationPrefixClass}--${size_1}`, className_1),
        ...contentProps_1
      });
    };
    $[6] = internalNotification;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[11] !== internalNotification) {
    t7 = t8 => {
      const {
        className: className_2,
        size: size_2,
        ...contentProps_2
      } = t8;
      internalNotification.warning({
        ...warningNotificationConfig,
        className: cx(warningNotificationConfig.className, size_2 && `${notificationPrefixClass}--${size_2}`, className_2),
        ...contentProps_2
      });
    };
    $[11] = internalNotification;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== internalNotification.open || $[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = {
      success: t3,
      error: t4,
      destroy: t5,
      info: t6,
      open: internalNotification.open,
      warning: t7
    };
    $[13] = internalNotification.open;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const notification = t8;
  let t9;
  if ($[20] !== message || $[21] !== modal || $[22] !== notification) {
    t9 = {
      message,
      modal,
      notification
    };
    $[20] = message;
    $[21] = modal;
    $[22] = notification;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
};
AntdApp.useApp = useApp;
AntdApp.displayName = App.displayName;